import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../apis/store";
import OrderLines from "../pages/order/OrderLines";
import Payment from "../pages/order/Payment";
import useIsConnected from "../../utils/IsConnected";
import { Button } from "antd";
import { setShowPaiement } from "../../apis/slices/globalSlice";
import { requestCartSync } from "../../apis/actions/cart.actions";
import { setAmount } from "../../apis/slices/hyperPaySlice";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { RiCloseLine, RiShoppingBag3Line } from "react-icons/ri";
import { getCartItemCount } from "../../utils/cartCount";
import { sumCartCatalogTotals } from "../../utils/effectivePrice";

interface Props {
  visible: boolean;
  setVisible: (v: boolean) => void;
}

const Cart = ({ visible, setVisible }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t, i18n } = useTranslation();
  const { services, products, showPaiement, vehicle, cartReady, cartLoading, cartError, cartTotal } = useSelector(
    (state: RootState) => state?.global
  );
  const isConnected = useIsConnected();
  const navigate = useNavigate();
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  const itemCount = useMemo(
    () => getCartItemCount(services, products),
    [services, products]
  );

  const hasServerCart = cartReady && !cartError;
  const isEmpty = hasServerCart && services.length === 0 && products.length === 0;

  const orderTotal = useMemo(
    () => cartTotal ?? sumCartCatalogTotals(services, products),
    [cartTotal, services, products]
  );

  useEffect(() => {
    if (visible && isConnected) dispatch(requestCartSync());
  }, [visible, isConnected, dispatch]);

  useEffect(() => {
    setVisible(false);
  }, [showPaiement]);

  useEffect(() => {
    if (!visible) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    previousFocus.current = document.activeElement as HTMLElement | null;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      previousFocus.current?.focus();
    };
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setVisible(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, setVisible]);

  return (
    <>
      <div
        className={`ac-canvas-backdrop${
          visible ? " ac-canvas-backdrop--open" : ""
        }`}
        aria-hidden="true"
        onClick={() => setVisible(false)}
      />

      <div
        className={`sidebar-canvas-wrapper ac-canvas--cart perfect-scrollbar button-bg-2${
          visible ? " sidebar-canvas-visible" : ""
        }`}
        role="dialog"
        aria-modal={visible}
        aria-labelledby="ac-cart-drawer-title"
        aria-hidden={!visible}
      >
        <div className="sidebar-canvas-container ac-cart-drawer__shell">
          <header className="sidebar-canvas-head ac-cart-drawer__head">
            <div className="ac-cart-drawer__topline">
              <span className="ac-cart-drawer__eyebrow">{t("profile.order.cart.orderOverview")}</span>
              <button ref={closeRef} type="button" className="ac-cart-drawer__close" onClick={() => setVisible(false)} aria-label={t("services.close")}><RiCloseLine aria-hidden="true" /></button>
            </div>
            <div className="ac-cart-drawer__hero">
              <span className="ac-cart-drawer__hero-icon" aria-hidden="true"><RiShoppingBag3Line /></span>
              <div className="ac-cart-drawer__head-text">
                <h2 id="ac-cart-drawer-title" className="ac-cart-drawer__title ac--font-expo">{t("profile.order.cart.cart")}</h2>
                <p className="ac-cart-drawer__subtitle">{t(hasServerCart ? isEmpty ? "services.cartIcon.emptyHeadline" : "profile.order.cart.reviewItems" : cartError ? "services.cartSync.failed" : "services.cartSync.loading")}</p>
              </div>
            </div>
            {hasServerCart && !isEmpty && <span className="ac-cart-drawer__count">{t("services.cartIcon.itemsLine", { count: itemCount })}</span>}
          </header>

          <div className="sidebar-canvas-content ac-cart-drawer__body">
            {isEmpty ? (
              <div className="ac-cart-drawer__empty">
                <div className="ac-cart-drawer__empty-visual" aria-hidden="true"><RiShoppingBag3Line /></div>
                <h3 className="ac-cart-drawer__empty-title">{t("services.cartIcon.emptyHeadline")}</h3>
                <p className="ac-cart-drawer__empty-lead">
                  {t("services.cartIcon.hintEmpty")}
                </p>
                <div className="ac-cart-drawer__empty-actions">
                  <Link
                    className="btn btn-primary rounded-3 ac-cart-drawer__empty-btn ac--font-expo"
                    to="/services"
                    onClick={() => setVisible(false)}
                  >
                    {t("menu.services")}
                  </Link>
                  <Link
                    className="btn rounded-3 ac-cart-drawer__empty-btn ac-cart-drawer__empty-btn--ghost ac--font-expo"
                    to="/products"
                    onClick={() => setVisible(false)}
                  >
                    {t("menu.products")}
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="ac-cart-drawer__scroll">
                  <div className="ac-cart-drawer__lines">
                    <OrderLines variant="drawer" />
                    {hasServerCart && services.length === 0 && products.length > 0 ? (
                      <div className="ac-cart-drawer__payment">
                        <h3 className="ac-cart-drawer__payment-title">{t("profile.order.cart.payment")}</h3>
                        <Payment
                          btnTitle={t("profile.order.cart.payment")}
                          toSave={(amount) => {
                            if (isConnected) {
                              dispatch(setShowPaiement(true));
                              dispatch(setAmount(amount));
                            } else {
                              navigate("/login");
                            }
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>

                {hasServerCart && services.length > 0 ? (
                  <footer className="ac-cart-drawer__footer">
                    <div className="ac-cart-drawer__total-row">
                      <span className="ac-cart-drawer__total-label">{t("profile.order.cart.total")}<small>{t("profile.order.cart.vat")}</small></span>
                      <strong className="ac-cart-drawer__total-value icon-saudi_riyal">{orderTotal}</strong>
                    </div>
                    <Button
                      type="primary"
                      size="large"
                      block
                      className="ac-cart-drawer__checkout-btn ac--btn-primary-jflatbold"
                      disabled={cartLoading || !vehicle?.model_id}
                      onClick={() => {
                        navigate(isConnected ? "/order" : "/login");
                        dispatch(setAmount(orderTotal));
                      }}
                    >
                      {t("profile.order.cart.choose")}
                      <span className="ac-cart-drawer__checkout-icon">
                        {i18n.language === "ar" ? (
                          <FaArrowLeft />
                        ) : (
                          <FaArrowRight />
                        )}
                      </span>
                    </Button>
                    {!vehicle?.model_id ? (
                      <div className="ac-cart-drawer__vehicle-hint">
                        <p className="ac-cart-drawer__vehicle-msg ac--font-expo">
                          {t("profile.order.cart.chooseCar")}
                        </p>
                        <button
                          type="button"
                          className="ac-cart-drawer__add-car ac--font-expo"
                          onClick={() => {
                            setVisible(false);
                            navigate("/add-car");
                          }}
                        >
                          {t("profile.order.cart.addYourCar")}
                        </button>
                      </div>
                    ) : null}
                  </footer>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Cart;
